/**
 * Messages camarades — chat étudiant ↔ étudiant (étape 1 : 1-1 texte).
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Send, Search, MessageSquare, X, Loader2, Paperclip, Users, UserPlus, LogOut, MoreVertical, Flag, Trash2, Ban, Lock, Play, Pause, Reply, Forward, Pin, Check, CheckCheck } from 'lucide-react';
import { AudioRecorder } from '../components/AudioRecorder';
import { EmojiPicker } from '../components/EmojiPicker';
import { PortailLayout } from '../PortailLayout';
import {
  portailApi,
  ChatConversationItem,
  ChatMessageItem,
  ChatEtudiantSummary,
  ChatEpingleItem,
} from '../services/portailApi';

function timeAgo(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso).getTime();
  const now = Date.now();
  const diff = now - d;
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'À l\'instant';
  if (min < 60) return `${min}min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h`;
  const j = Math.floor(h / 24);
  if (j < 7) return `${j}j`;
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
}

function formatHeureMessage(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

interface MessagesCamaradesProps {
  /** Si true, n'enveloppe pas dans PortailLayout (utilisé par Messagerie.tsx). */
  embedded?: boolean;
}

export default function MessagesCamarades({ embedded = false }: MessagesCamaradesProps = {}) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const initialConv = params.get('conv') ? Number(params.get('conv')) : null;

  const [conversations, setConversations] = useState<ChatConversationItem[]>([]);
  const [loadingConvs, setLoadingConvs] = useState(true);
  const [selectedConvId, setSelectedConvId] = useState<number | null>(initialConv);

  // Fix 2026-08-31 : synchroniser selectedConvId avec ?conv=X quand la
  // query change. Sans ça, cliquer sur un camarade depuis /portail/en-ligne
  // alors qu'on est déjà sur /portail/messagerie n'ouvrait pas la nouvelle
  // conversation (React Router garde le composant monté → useState ne
  // se réévalue plus).
  useEffect(() => {
    const q = params.get('conv');
    const target = q ? Number(q) : null;
    if (target && target !== selectedConvId) {
      setSelectedConvId(target);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.get('conv')]);
  const [messages, setMessages] = useState<ChatMessageItem[]>([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [convInfo, setConvInfo] = useState<{
    type: string; nom: string;
    autres_participants: ChatEtudiantSummary[];
    en_ligne: boolean;
  } | null>(null);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [audioPhase, setAudioPhase] = useState<'idle' | 'recording' | 'sending'>('idle');
  const [showAnnuaire, setShowAnnuaire] = useState(false);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [photoModal, setPhotoModal] = useState<ChatEtudiantSummary | null>(null);
  const [replyTo, setReplyTo] = useState<ChatMessageItem | null>(null);
  const [messageMenu, setMessageMenu] = useState<ChatMessageItem | null>(null);
  const [forwardMsg, setForwardMsg] = useState<ChatMessageItem | null>(null);
  const [convFilter, setConvFilter] = useState<'toutes' | 'non_lues' | 'groupes'>('toutes');
  // Id de la conversation dont le menu d'options par-ligne est ouvert.
  const [convItemMenu, setConvItemMenu] = useState<number | null>(null);
  const [showEmoji, setShowEmoji] = useState(false);
  const [typingOthers, setTypingOthers] = useState<{ matricule: string; action: 'typing' | 'recording' }[]>([]);
  const lastTypingPingRef = useRef(0);
  const [epingles, setEpingles] = useState<ChatEpingleItem[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Tracker pour ne pas casser la position de scroll pendant le polling.
  const wasAtBottomRef = useRef(true);
  const lastMessageIdRef = useRef<number | null>(null);

  // Détecte si on est proche du bas (< 80px) avant chaque refresh.
  const checkAtBottom = () => {
    const el = messagesContainerRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  // Charger la liste des conversations
  const loadConvs = async () => {
    setLoadingConvs(true);
    try {
      const res = await portailApi.chatConversations();
      setConversations(res.results || []);
    } catch (e: any) {
      // ignore
    } finally {
      setLoadingConvs(false);
    }
  };

  useEffect(() => {
    loadConvs();
    const i = setInterval(loadConvs, 15000);
    return () => clearInterval(i);
  }, []);

  // Charger une conversation
  // forceScroll=true → scroll en bas systématiquement (chargement initial, envoi).
  // Sinon : on respecte la position de l'utilisateur, on scrolle que s'il est déjà en bas.
  const loadMessages = async (convId: number, forceScroll = false) => {
    // Capture la position AVANT le refresh (pendant le polling).
    const wasAtBottom = forceScroll || checkAtBottom();
    setLoadingMsgs(true);
    try {
      const res = await portailApi.chatMessages(convId);
      const newMessages = res.messages || [];
      setMessages(newMessages);
      setConvInfo(res.conversation);
      setEpingles(res.epingles || []);
      setTypingOthers(res.typing || []);

      // Décrémenter le badge non_lus localement (la lecture est marquée côté serveur).
      setConversations(prev => prev.map(c => c.id === convId ? { ...c, nb_non_lus: 0 } : c));

      // Scroll uniquement si :
      //  - chargement initial / envoi (forceScroll)
      //  - l'utilisateur était déjà en bas (lecture en temps réel)
      //  - un NOUVEAU message est arrivé ET on est en bas
      const lastId = newMessages.length ? newMessages[newMessages.length - 1].id : null;
      const newMessageArrived = lastId !== lastMessageIdRef.current;
      lastMessageIdRef.current = lastId;
      if (forceScroll || (wasAtBottom && newMessageArrived)) {
        setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: forceScroll ? 'auto' : 'smooth' }), 50);
      }
    } catch (e: any) {
      setMessages([]);
      setEpingles([]);
    } finally {
      setLoadingMsgs(false);
    }
  };

  useEffect(() => {
    if (selectedConvId) {
      // Reset trackers à l'ouverture d'une nouvelle conv
      lastMessageIdRef.current = null;
      wasAtBottomRef.current = true;
      setTypingOthers([]);
      loadMessages(selectedConvId, true);  // force scroll au chargement initial
      const i = setInterval(() => loadMessages(selectedConvId, false), 8000);
      return () => {
        clearInterval(i);
        // Stop signal typing en sortant de la conv
        portailApi.chatTyping(selectedConvId, 'stop').catch(() => {});
      };
    }
  }, [selectedConvId]);

  const envoyer = async () => {
    if (!input.trim() || !selectedConvId || sending) return;
    setSending(true);
    // Optimistic UI : insère le message immédiatement (WhatsApp-like)
    const contenu = input.trim();
    const replySnapshot = replyTo;
    const tempId = -Date.now();
    const optimisticMsg: ChatMessageItem = {
      id: tempId,
      expediteur: { matricule: '', nom: '', prenoms: '', photo_url: null, classe: '' },
      est_de_moi: true,
      type: 'texte',
      contenu,
      fichier_url: null,
      fichier_nom: '',
      date_envoi: new Date().toISOString(),
      supprime: false,
      statut: 'envoye',
      reply: replySnapshot ? {
        id: replySnapshot.id,
        expediteur_nom: replySnapshot.est_de_moi ? 'Vous' : `${replySnapshot.expediteur.prenoms} ${replySnapshot.expediteur.nom}`,
        est_de_moi: replySnapshot.est_de_moi,
        type: replySnapshot.type,
        apercu: (replySnapshot.contenu || '').slice(0, 120),
        supprime: replySnapshot.supprime,
      } : null,
    };
    setMessages(prev => [...prev, optimisticMsg]);
    setInput('');
    setReplyTo(null);
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 30);

    try {
      await portailApi.chatEnvoyerMessage(selectedConvId, contenu, replySnapshot?.id);
      // Re-sync depuis le serveur (remplace le temp par le vrai message avec son ID)
      await loadMessages(selectedConvId, false);  // pas de force scroll : on y est déjà
      loadConvs();
    } catch (e: any) {
      // Rollback en cas d'erreur réseau
      setMessages(prev => prev.filter(m => m.id !== tempId));
      setInput(contenu);
      setReplyTo(replySnapshot);
      alert(e?.message || 'Erreur envoi');
    } finally {
      setSending(false);
    }
  };

  const epinglerMessage = async (msgId: number) => {
    try {
      await portailApi.chatEpinglerMessage(msgId);
      setMessageMenu(null);
      if (selectedConvId) loadMessages(selectedConvId);
    } catch (e: any) {
      alert(e?.message || 'Erreur');
    }
  };

  const scrollToMessage = (msgId: number) => {
    const el = document.getElementById(`msg-${msgId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('ring-2', 'ring-yellow-400');
      setTimeout(() => el.classList.remove('ring-2', 'ring-yellow-400'), 1500);
    }
  };

  const ouvrirConv = async (matricule: string) => {
    try {
      const res = await portailApi.chatOuvrirDirecte(matricule);
      setSelectedConvId(res.conversation_id);
      setParams({ conv: String(res.conversation_id) }, { replace: true });
      setShowAnnuaire(false);
      loadConvs();
    } catch (e: any) {
      alert(e?.message || 'Erreur');
    }
  };

  // Compression image client-side (WhatsApp-like : <300 KB, max 1600px côté)
  const compressImage = (file: File): Promise<File> => new Promise((resolve) => {
    if (!file.type.startsWith('image/')) return resolve(file);
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const MAX_DIM = 1600;
      let { width, height } = img;
      if (width > MAX_DIM || height > MAX_DIM) {
        if (width > height) { height = (height * MAX_DIM) / width; width = MAX_DIM; }
        else { width = (width * MAX_DIM) / height; height = MAX_DIM; }
      }
      const canvas = document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx?.drawImage(img, 0, 0, width, height);
      canvas.toBlob((blob) => {
        URL.revokeObjectURL(url);
        if (!blob) return resolve(file);
        // Si la compression empire le fichier, garder l'original
        const out = blob.size < file.size
          ? new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' })
          : file;
        resolve(out);
      }, 'image/jpeg', 0.82);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });

  const envoyerFichier = async (file: File) => {
    if (!selectedConvId || sending) return;
    if (file.size > 10 * 1024 * 1024) {
      alert('Fichier trop lourd (max 10 Mo)');
      return;
    }
    setSending(true);
    // Optimistic UI : insère un message provisoire immédiatement
    const previewUrl = file.type.startsWith('image/') ? URL.createObjectURL(file) : null;
    const tempId = -Date.now();
    const optimisticMsg: ChatMessageItem = {
      id: tempId,
      expediteur: { matricule: '', nom: '', prenoms: '', photo_url: null, classe: '' },
      est_de_moi: true,
      type: file.type.startsWith('image/') ? 'image' : 'fichier',
      contenu: input.trim(),
      fichier_url: previewUrl,
      fichier_nom: file.name,
      date_envoi: new Date().toISOString(),
      supprime: false,
      statut: 'envoye',
    };
    setMessages(prev => [...prev, optimisticMsg]);
    const inputBackup = input;
    setInput('');
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 30);

    try {
      // Compression image avant upload (réduit 70-90% le poids)
      const finalFile = await compressImage(file);
      await portailApi.chatEnvoyerFichier(selectedConvId, finalFile, inputBackup.trim());
      await loadMessages(selectedConvId, true);
      loadConvs();
    } catch (e: any) {
      // Rollback : retire le message optimiste, remet l'input
      setMessages(prev => prev.filter(m => m.id !== tempId));
      setInput(inputBackup);
      alert(e?.message || 'Erreur envoi fichier');
    } finally {
      setSending(false);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    }
  };

  const envoyerAudio = async (blob: Blob, dureeSec: number) => {
    if (!selectedConvId) throw new Error('Aucune conversation sélectionnée');
    await portailApi.chatEnvoyerAudio(selectedConvId, blob, dureeSec);
    await loadMessages(selectedConvId, true);
    loadConvs();
  };

  const quitterGroupe = async () => {
    if (!selectedConvId || !convInfo || convInfo.type !== 'groupe') return;
    if (!window.confirm(`Quitter le groupe « ${convInfo.nom} » ?`)) return;
    try {
      await portailApi.chatQuitterGroupe(selectedConvId);
      setSelectedConvId(null);
      setParams({}, { replace: true });
      setShowMenu(false);
      loadConvs();
    } catch (e: any) {
      alert(e?.message || 'Erreur');
    }
  };

  const supprimerConversation = async (convId: number, titre?: string) => {
    const nom = titre || conversations.find(c => c.id === convId)?.titre || 'cette conversation';
    if (!window.confirm(
      `Supprimer « ${nom} » de votre liste ?\n\n`
      + 'La conversation reste enregistrée pour l\'administration. '
      + 'Elle réapparaîtra automatiquement si vous recevez un nouveau message.'
    )) return;
    try {
      await portailApi.chatSupprimerConversation(convId);
      setConversations(prev => prev.filter(c => c.id !== convId));
      if (selectedConvId === convId) {
        setSelectedConvId(null);
        setParams({}, { replace: true });
        setShowMenu(false);
      }
    } catch (e: any) {
      alert(e?.message || 'Erreur');
    }
  };

  const bloquerEtudiant = async () => {
    if (!convInfo || convInfo.type !== 'direct' || !convInfo.autres_participants[0]) return;
    const cible = convInfo.autres_participants[0];
    const motif = window.prompt(
      `Bloquer ${cible.prenoms} ${cible.nom} ?\n\n`
      + 'Vous ne pourrez plus échanger de messages avec cette personne.\n\n'
      + 'Motif (facultatif) :'
    );
    if (motif === null) return;
    try {
      await portailApi.chatBloquer(cible.matricule_reel || cible.matricule, motif || undefined);
      alert('Étudiant bloqué.');
      setShowMenu(false);
      setSelectedConvId(null);
      setParams({}, { replace: true });
      loadConvs();
    } catch (e: any) {
      alert(e?.message || 'Erreur');
    }
  };

  const supprimerMessage = async (msgId: number) => {
    if (!window.confirm('Supprimer ce message ?')) return;
    try {
      await portailApi.chatSupprimerMessage(msgId);
      if (selectedConvId) loadMessages(selectedConvId);
    } catch (e: any) {
      alert(e?.message || 'Erreur');
    }
  };

  const signalerMessage = async (msgId: number) => {
    const motif = window.prompt(
      'Signaler ce message à la modération ?\n\n'
      + 'Motif (5 caractères minimum) :'
    );
    if (!motif || motif.trim().length < 5) return;
    try {
      await portailApi.chatSignalerMessage(msgId, motif.trim());
      alert('Message signalé. La scolarité va l\'examiner.');
    } catch (e: any) {
      alert(e?.message || 'Erreur');
    }
  };

  // Vue mobile : si conversation sélectionnée, on cache la liste
  const showList = !selectedConvId || !convInfo;
  const showChat = !!selectedConvId;

  const mainContent = (
    <>
      <div className={embedded
        ? 'flex flex-col h-[calc(100vh-240px)] sm:h-[calc(100vh-220px)]'
        : 'flex flex-col h-[calc(100vh-180px)] sm:h-[calc(100vh-160px)]'}>
        <div className="flex-1 flex overflow-hidden gap-2">
          {/* Liste des conversations */}
          {(showList || !showChat) && (
            <div className={`${showChat ? 'hidden sm:flex' : 'flex'} flex-col w-full sm:w-72 sm:max-w-[16rem] bg-gray-900 border border-gray-800 rounded-lg overflow-hidden`}>
              <div className="px-3 py-2 border-b border-gray-800 flex items-center justify-between gap-1">
                <button
                  onClick={() => navigate('/portail/accueil')}
                  className="p-1 -ml-1 text-gray-400 hover:text-white hover:bg-gray-800 rounded"
                  title="Retour à l'accueil"
                  aria-label="Retour"
                >
                  <ArrowLeft size={16} />
                </button>
                <h3 className="text-sm font-semibold text-gray-100 flex-1 truncate">Conversations</h3>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setShowCreateGroup(true)}
                    title="Créer un groupe"
                    className="text-xs px-2 py-1 bg-purple-600 hover:bg-purple-700 text-white rounded inline-flex items-center gap-1"
                  >
                    <Users size={12} /> Groupe
                  </button>
                  <button
                    onClick={() => setShowAnnuaire(true)}
                    title="Nouvelle discussion 1-1"
                    className="text-xs px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded inline-flex items-center gap-1"
                  >
                    <Search size={12} /> 1-1
                  </button>
                </div>
              </div>
              {/* Filtres Toutes / Non lues / Groupes (style WhatsApp) */}
              <div className="px-2 py-1.5 border-b border-gray-800 flex items-center gap-1 overflow-x-auto">
                {([
                  { key: 'toutes', label: 'Toutes' },
                  { key: 'non_lues', label: 'Non lues' },
                  { key: 'groupes', label: 'Groupes' },
                ] as const).map(t => {
                  const count = t.key === 'non_lues'
                    ? conversations.filter(c => c.nb_non_lus > 0).length
                    : t.key === 'groupes'
                      ? conversations.filter(c => c.type === 'groupe').length
                      : conversations.length;
                  return (
                    <button
                      key={t.key}
                      onClick={() => setConvFilter(t.key)}
                      className={`px-2.5 py-1 rounded-full text-[11px] font-medium whitespace-nowrap transition-colors ${
                        convFilter === t.key
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                      }`}
                    >
                      {t.label}{count > 0 && (
                        <span className="ml-1 opacity-80">({count})</span>
                      )}
                    </button>
                  );
                })}
              </div>
              <div className="flex-1 overflow-y-auto">
                {loadingConvs && <div className="p-4 text-xs text-gray-500 text-center">Chargement…</div>}
                {!loadingConvs && conversations.length === 0 && (
                  <div className="p-6 text-xs text-gray-500 text-center">
                    <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    Aucune conversation. Commencez en cliquant sur "Nouvelle".
                  </div>
                )}
                {conversations
                  .filter(c => (
                    convFilter === 'toutes' ? true :
                    convFilter === 'non_lues' ? c.nb_non_lus > 0 :
                    convFilter === 'groupes' ? c.type === 'groupe' : true
                  ))
                  .map((c) => (
                  <div
                    key={c.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      setConvItemMenu(null);
                      setSelectedConvId(c.id);
                      setParams({ conv: String(c.id) }, { replace: true });
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setConvItemMenu(null);
                        setSelectedConvId(c.id);
                        setParams({ conv: String(c.id) }, { replace: true });
                      }
                    }}
                    className={`group relative w-full text-left px-3 py-2.5 border-b border-gray-800 hover:bg-gray-800/50 transition-colors cursor-pointer ${
                      selectedConvId === c.id ? 'bg-blue-900/30' : ''
                    }`}
                  >
                    <div className="flex items-center gap-2.5">
                      <div className="relative">
                        {c.avatar_url ? (
                          <img src={c.avatar_url} alt="" className="w-9 h-9 rounded-full object-cover" />
                        ) : (
                          <div className="w-9 h-9 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-semibold">
                            {c.titre.split(' ').map(s => s[0]).slice(0, 2).join('')}
                          </div>
                        )}
                        {c.en_ligne && <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 border-2 border-gray-900 rounded-full" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-medium text-gray-100 truncate">{c.titre}</span>
                          <span className="text-[10px] text-gray-500 flex-shrink-0">{timeAgo(c.dernier_message?.date_envoi)}</span>
                        </div>
                        <div className="flex items-center justify-between gap-2 mt-0.5">
                          <span className="text-xs text-gray-400 truncate">
                            {c.dernier_message
                              ? (c.dernier_message.est_de_moi ? 'Vous : ' : '') + (c.dernier_message.supprime ? '(message supprimé)' : c.dernier_message.contenu)
                              : <span className="italic">Démarrer la conversation</span>}
                          </span>
                          {c.nb_non_lus > 0 && (
                            <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] text-[10px] font-bold bg-blue-500 text-white rounded-full px-1">
                              {c.nb_non_lus}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Bouton menu (3 points) — toujours visible sur tactile, opacité 0 → 100 au survol sur desktop */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setConvItemMenu(convItemMenu === c.id ? null : c.id);
                      }}
                      title="Options de la conversation"
                      aria-label="Options"
                      className="absolute right-2 top-2 p-1 rounded text-gray-400 hover:text-white hover:bg-gray-700/70 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
                    >
                      <MoreVertical size={16} />
                    </button>

                    {convItemMenu === c.id && (
                      <>
                        {/* Overlay pour fermer en cliquant ailleurs */}
                        <div
                          className="fixed inset-0 z-20"
                          onClick={(e) => { e.stopPropagation(); setConvItemMenu(null); }}
                        />
                        <div
                          className="absolute right-2 top-9 z-30 bg-gray-800 border border-gray-700 rounded-lg shadow-xl min-w-[200px]"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setConvItemMenu(null);
                              supprimerConversation(c.id, c.titre);
                            }}
                            className="w-full px-3 py-2 text-left text-sm text-red-400 hover:bg-gray-700 flex items-center gap-2 rounded-lg"
                          >
                            <Trash2 size={14} /> Supprimer la conversation
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Fil de messages */}
          {showChat && (
            <div className="flex-1 flex flex-col bg-gray-900 border border-gray-800 rounded-lg overflow-hidden">
              {convInfo ? (
                <>
                  <div className="px-3 py-2 border-b border-gray-800 flex items-center gap-2.5 relative">
                    <button
                      onClick={() => { setSelectedConvId(null); setParams({}, { replace: true }); }}
                      className="sm:hidden p-1 text-gray-400 hover:text-white"
                    >
                      <ArrowLeft size={18} />
                    </button>
                    {convInfo.type === 'groupe' ? (
                      <>
                        <div className="w-9 h-9 rounded-full bg-purple-600 text-white flex items-center justify-center flex-shrink-0">
                          <Users size={16} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-gray-100 truncate">{convInfo.nom}</div>
                          <div className="text-[11px] truncate">
                            {typingOthers.length > 0 ? (
                              <span className="text-blue-400 italic">
                                {typingOthers.length === 1
                                  ? (typingOthers[0].action === 'recording' ? '🎤 enregistre un vocal…' : 'en train d\'écrire…')
                                  : `${typingOthers.length} personnes écrivent…`}
                              </span>
                            ) : (
                              <span className="text-gray-400">{convInfo.autres_participants.length + 1} membres</span>
                            )}
                          </div>
                        </div>
                      </>
                    ) : convInfo.autres_participants[0] && (
                      <>
                        <button
                          type="button"
                          onClick={() => setPhotoModal(convInfo.autres_participants[0])}
                          className="flex-shrink-0 group"
                          aria-label="Voir la photo"
                        >
                          {convInfo.autres_participants[0].photo_url ? (
                            <img src={convInfo.autres_participants[0].photo_url} alt="" className="w-9 h-9 rounded-full object-cover ring-0 group-hover:ring-2 ring-blue-400 transition" />
                          ) : (
                            <div className="w-9 h-9 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-semibold">
                              {(convInfo.autres_participants[0].prenoms[0] || '') + (convInfo.autres_participants[0].nom[0] || '')}
                            </div>
                          )}
                        </button>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-gray-100 truncate">
                            {convInfo.autres_participants[0].prenoms} {convInfo.autres_participants[0].nom}
                          </div>
                          <div className="text-[11px] truncate">
                            {typingOthers.length > 0 ? (
                              <span className="text-blue-400 italic">
                                {typingOthers[0].action === 'recording' ? '🎤 enregistre un vocal…' : 'en train d\'écrire…'}
                              </span>
                            ) : convInfo.en_ligne ? (
                              <span className="text-green-400">● En ligne</span>
                            ) : (
                              <span className="text-gray-400">{convInfo.autres_participants[0].classe}</span>
                            )}
                          </div>
                        </div>
                      </>
                    )}
                    <button
                      onClick={() => setShowMenu(!showMenu)}
                      className="p-1.5 text-gray-400 hover:text-white"
                      title="Options"
                    >
                      <MoreVertical size={18} />
                    </button>
                    {showMenu && (
                      <div className="absolute right-2 top-full mt-1 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-20 min-w-[200px]">
                        {selectedConvId && (
                          <button
                            onClick={() => supprimerConversation(selectedConvId)}
                            className="w-full px-3 py-2 text-left text-sm text-gray-200 hover:bg-gray-700 flex items-center gap-2"
                          >
                            <Trash2 size={14} /> Supprimer la conversation
                          </button>
                        )}
                        {convInfo.type === 'direct' && (
                          <button
                            onClick={bloquerEtudiant}
                            className="w-full px-3 py-2 text-left text-sm text-red-400 hover:bg-gray-700 flex items-center gap-2"
                          >
                            <Ban size={14} /> Bloquer cette personne
                          </button>
                        )}
                        {convInfo.type === 'groupe' && (
                          <button
                            onClick={quitterGroupe}
                            className="w-full px-3 py-2 text-left text-sm text-red-400 hover:bg-gray-700 flex items-center gap-2"
                          >
                            <LogOut size={14} /> Quitter le groupe
                          </button>
                        )}
                        {convInfo.type === 'groupe' && (
                          <div className="px-3 py-2 text-[11px] text-gray-500 border-t border-gray-700">
                            <div className="font-semibold text-gray-300 mb-1">Membres :</div>
                            {convInfo.autres_participants.map(p => (
                              <div key={p.matricule_reel || p.matricule} className="truncate">{p.prenoms} {p.nom}</div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Bandeau épingles */}
                  {epingles.length > 0 && (
                    <div className="border-b border-gray-800 bg-gray-900/50 px-3 py-1.5 max-h-24 overflow-y-auto">
                      {epingles.map(ep => (
                        <button
                          key={ep.id}
                          onClick={() => scrollToMessage(ep.id)}
                          className="w-full text-left flex items-start gap-2 px-2 py-1 hover:bg-gray-800/50 rounded text-[11px]"
                          title="Aller au message épinglé"
                        >
                          <Pin size={11} className="text-yellow-400 mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <span className="text-yellow-400 font-semibold">{ep.expediteur_nom}</span>
                            <span className="text-gray-400 ml-1.5 truncate">{ep.apercu || '(message)'}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Messages */}
                  <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-3 space-y-2">
                    {loadingMsgs && messages.length === 0 && (
                      <div className="text-center py-8 text-xs text-gray-500"><Loader2 className="inline w-4 h-4 animate-spin mr-1" /> Chargement…</div>
                    )}
                    {!loadingMsgs && messages.length === 0 && (
                      <div className="text-center py-8 text-xs text-gray-500">
                        Aucun message. Soyez le premier à dire bonjour !
                      </div>
                    )}
                    {messages.map((m) => (
                      <div key={m.id} id={`msg-${m.id}`} className={`flex ${m.est_de_moi ? 'justify-end' : 'justify-start'} items-end gap-1.5 rounded transition-shadow`}>
                        {!m.est_de_moi && convInfo.type === 'groupe' && (
                          <button
                            type="button"
                            onClick={() => setPhotoModal(m.expediteur)}
                            className="flex-shrink-0"
                            aria-label="Voir la photo"
                          >
                            {m.expediteur.photo_url ? (
                              <img src={m.expediteur.photo_url} alt="" className="w-6 h-6 rounded-full object-cover" />
                            ) : (
                              <div className="w-6 h-6 rounded-full bg-blue-500 text-white text-[10px] font-bold flex items-center justify-center">
                                {(m.expediteur.prenoms?.[0] || '') + (m.expediteur.nom?.[0] || '')}
                              </div>
                            )}
                          </button>
                        )}
                        <div className={`group relative max-w-[75%] rounded-lg px-3 py-2 ${
                          m.est_de_moi
                            ? 'bg-blue-600 text-white rounded-br-sm'
                            : 'bg-gray-800 text-gray-100 rounded-bl-sm'
                        }`}>
                          {!m.supprime && (
                            <button
                              onClick={() => setMessageMenu(m)}
                              title="Actions"
                              className={`absolute ${m.est_de_moi ? '-left-8' : '-right-8'} top-1 w-7 h-7 flex items-center justify-center bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity`}
                            >
                              <MoreVertical size={13} />
                            </button>
                          )}
                          {!m.est_de_moi && convInfo.type === 'groupe' && (
                            <div className="text-[10px] font-semibold text-blue-300 mb-0.5">
                              {m.expediteur.prenoms} {m.expediteur.nom}
                            </div>
                          )}
                          {/* Reply quote */}
                          {m.reply && !m.supprime && (
                            <button
                              onClick={() => scrollToMessage(m.reply!.id)}
                              className={`w-full text-left mb-1.5 px-2 py-1 border-l-2 rounded-r text-xs ${
                                m.est_de_moi
                                  ? 'bg-blue-800/40 border-blue-300'
                                  : 'bg-gray-700/60 border-gray-400'
                              }`}
                            >
                              <div className="font-semibold opacity-80">{m.reply.expediteur_nom}</div>
                              <div className="opacity-70 truncate">
                                {m.reply.supprime ? '(message supprimé)' : (m.reply.apercu || `(${m.reply.type})`)}
                              </div>
                            </button>
                          )}
                          {/* Indicateur transféré */}
                          {m.transfere_de && !m.supprime && (
                            <div className={`text-[10px] italic flex items-center gap-1 mb-1 ${m.est_de_moi ? 'text-blue-200' : 'text-gray-400'}`}>
                              <Forward size={10} /> Transféré
                            </div>
                          )}
                          {/* Indicateur épinglé */}
                          {m.epingle && !m.supprime && (
                            <div className={`text-[10px] flex items-center gap-1 mb-1 ${m.est_de_moi ? 'text-yellow-200' : 'text-yellow-400'}`}>
                              <Pin size={10} /> Épinglé
                            </div>
                          )}
                          {m.supprime ? (
                            <div className="italic text-xs opacity-70">(Message supprimé)</div>
                          ) : (
                            <>
                              {m.type === 'image' && m.fichier_url && (
                                <a href={m.fichier_url} target="_blank" rel="noopener noreferrer">
                                  <img
                                    src={m.fichier_url}
                                    alt={m.fichier_nom || 'image'}
                                    className="rounded mb-1 max-h-64 max-w-full object-contain"
                                  />
                                </a>
                              )}
                              {m.type === 'fichier' && m.fichier_url && (
                                <a
                                  href={m.fichier_url}
                                  target="_blank" rel="noopener noreferrer"
                                  className={`flex items-center gap-2 px-2 py-1.5 rounded mb-1 ${
                                    m.est_de_moi ? 'bg-blue-700 hover:bg-blue-800' : 'bg-gray-700 hover:bg-gray-600'
                                  }`}
                                >
                                  <Paperclip size={14} className="flex-shrink-0" />
                                  <span className="text-xs truncate underline">{m.fichier_nom || 'Fichier'}</span>
                                </a>
                              )}
                              {m.type === 'audio' && m.fichier_url && (
                                <AudioMessage
                                  url={m.fichier_url}
                                  dureeSec={m.duree_audio_sec || 0}
                                  estDeMoi={m.est_de_moi}
                                />
                              )}
                              {m.contenu && (
                                <div className="text-sm whitespace-pre-wrap break-words">{m.contenu}</div>
                              )}
                            </>
                          )}
                          <div className={`text-[10px] mt-1 flex items-center gap-1 ${m.est_de_moi ? 'text-blue-100 justify-end' : 'text-gray-400'}`}>
                            <span>{formatHeureMessage(m.date_envoi)}</span>
                            {/* Statut de lecture (sur mes propres messages) */}
                            {m.est_de_moi && m.statut && (
                              m.statut === 'lu' ? (
                                <CheckCheck size={13} className="text-sky-200" />
                              ) : m.statut === 'lu_partiel' ? (
                                <CheckCheck size={13} className="text-blue-200/60" />
                              ) : (
                                <Check size={13} className="text-blue-200/60" />
                              )
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Composer — AudioRecorder TOUJOURS monté pour ne pas perdre l'état pendant l'enregistrement */}
                  <div className="border-t border-gray-800 p-2">
                    {/* Reply preview */}
                    {replyTo && (
                      <div className="mb-2 flex items-start gap-2 bg-gray-800 border-l-2 border-blue-500 rounded-r px-2 py-1.5">
                        <Reply size={12} className="text-blue-400 mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0 text-[11px]">
                          <div className="font-semibold text-blue-300">
                            {replyTo.est_de_moi ? 'Vous' : `${replyTo.expediteur.prenoms} ${replyTo.expediteur.nom}`}
                          </div>
                          <div className="text-gray-400 truncate">
                            {replyTo.contenu || (
                              replyTo.type === 'image' ? '📷 Image' :
                              replyTo.type === 'audio' ? '🎤 Message vocal' :
                              replyTo.type === 'fichier' ? `📎 ${replyTo.fichier_nom}` : ''
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => setReplyTo(null)}
                          className="text-gray-400 hover:text-white p-0.5 flex-shrink-0"
                          title="Annuler la réponse"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt"
                        className="hidden"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) envoyerFichier(f);
                          e.target.value = '';
                        }}
                      />
                      {/* Paperclip + emoji + champ texte : visibles uniquement en phase idle */}
                      {audioPhase === 'idle' && (
                        <>
                          <button
                            onClick={() => fileInputRef.current?.click()}
                            disabled={sending}
                            className="w-9 h-9 flex items-center justify-center text-gray-400 hover:text-blue-400 hover:bg-gray-800 rounded-full disabled:opacity-50 flex-shrink-0"
                            title="Envoyer une photo / fichier"
                          >
                            <Paperclip size={18} />
                          </button>
                          <div className="relative">
                            <button
                              onClick={() => setShowEmoji(v => !v)}
                              disabled={sending}
                              className="w-9 h-9 flex items-center justify-center text-gray-400 hover:text-yellow-400 hover:bg-gray-800 rounded-full disabled:opacity-50 flex-shrink-0"
                              title="Emojis"
                              type="button"
                            >
                              <span className="text-lg leading-none">😊</span>
                            </button>
                            {showEmoji && (
                              <EmojiPicker
                                onPick={(e) => { setInput(prev => prev + e); }}
                                onClose={() => setShowEmoji(false)}
                              />
                            )}
                          </div>
                          <input
                            type="text"
                            value={input}
                            onChange={(e) => {
                              setInput(e.target.value);
                              // Ping typing toutes les 3s tant que l'utilisateur saisit (TTL serveur=5s)
                              const now = Date.now();
                              if (selectedConvId && e.target.value.length > 0 && now - lastTypingPingRef.current > 3000) {
                                lastTypingPingRef.current = now;
                                portailApi.chatTyping(selectedConvId, 'typing').catch(() => {});
                              }
                            }}
                            onBlur={() => {
                              if (selectedConvId) portailApi.chatTyping(selectedConvId, 'stop').catch(() => {});
                              lastTypingPingRef.current = 0;
                            }}
                            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); envoyer(); } }}
                            placeholder="Tapez votre message…"
                            maxLength={5000}
                            className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-full text-sm text-gray-100 placeholder-gray-500 focus:outline-none focus:border-blue-500"
                          />
                        </>
                      )}
                      {/* Bouton Send texte : visible uniquement si idle + texte saisi */}
                      {audioPhase === 'idle' && input.trim() && (
                        <button
                          onClick={envoyer}
                          disabled={sending}
                          className="w-9 h-9 flex items-center justify-center bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-full transition-colors flex-shrink-0"
                          title="Envoyer"
                        >
                          {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                        </button>
                      )}
                      {/* AudioRecorder : monté en permanence — masqué visuellement seulement
                          si idle + texte saisi (mais reste dans le DOM pour conserver son état) */}
                      <div className={`${audioPhase !== 'idle' ? 'flex-1' : ''} ${audioPhase === 'idle' && input.trim() ? 'hidden' : ''}`}>
                        <AudioRecorder
                          onRecorded={envoyerAudio}
                          onPhaseChange={(phase) => {
                            setAudioPhase(phase);
                            if (selectedConvId) {
                              if (phase === 'recording') {
                                portailApi.chatTyping(selectedConvId, 'recording').catch(() => {});
                              } else if (phase === 'idle') {
                                portailApi.chatTyping(selectedConvId, 'stop').catch(() => {});
                              }
                            }
                          }}
                          disabled={sending}
                        />
                      </div>
                    </div>
                    <div className="mt-1.5 flex items-center justify-center gap-1 text-[10px] text-gray-500">
                      <Lock size={10} />
                      <span>Communications sécurisées (HTTPS)</span>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-sm text-gray-500">
                  Sélectionnez une conversation
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {showAnnuaire && (
        <AnnuaireModal
          onClose={() => setShowAnnuaire(false)}
          onSelect={ouvrirConv}
        />
      )}

      {showCreateGroup && (
        <CreateGroupModal
          onClose={() => setShowCreateGroup(false)}
          onCreated={(convId) => {
            setShowCreateGroup(false);
            setSelectedConvId(convId);
            setParams({ conv: String(convId) }, { replace: true });
            loadConvs();
          }}
        />
      )}

      {photoModal && (
        <PhotoModal etu={photoModal} onClose={() => setPhotoModal(null)} />
      )}

      {messageMenu && (
        <MessageActionsSheet
          msg={messageMenu}
          onClose={() => setMessageMenu(null)}
          onReply={() => { setReplyTo(messageMenu); setMessageMenu(null); }}
          onForward={() => { setForwardMsg(messageMenu); setMessageMenu(null); }}
          onEpingler={() => epinglerMessage(messageMenu.id)}
          onSupprimer={() => { supprimerMessage(messageMenu.id); setMessageMenu(null); }}
          onSignaler={() => { signalerMessage(messageMenu.id); setMessageMenu(null); }}
        />
      )}

      {forwardMsg && (
        <ForwardModal
          msg={forwardMsg}
          conversations={conversations.filter(c => c.id !== selectedConvId)}
          onClose={() => setForwardMsg(null)}
          onSent={() => { setForwardMsg(null); }}
        />
      )}
    </>
  );
  return embedded
    ? mainContent
    : <PortailLayout showHeader title="Messages camarades">{mainContent}</PortailLayout>;
}

// ─── Feuille d'actions message (Répondre, Transférer, Épingler, etc.) ─────
const MessageActionsSheet: React.FC<{
  msg: ChatMessageItem;
  onClose: () => void;
  onReply: () => void;
  onForward: () => void;
  onEpingler: () => void;
  onSupprimer: () => void;
  onSignaler: () => void;
}> = ({ msg, onClose, onReply, onForward, onEpingler, onSupprimer, onSignaler }) => {
  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-[55] bg-black/50 flex items-end sm:items-center justify-center"
    >
      <div
        onClick={e => e.stopPropagation()}
        className="bg-gray-900 border border-gray-800 rounded-t-xl sm:rounded-xl w-full sm:max-w-sm overflow-hidden"
      >
        <div className="px-4 py-3 border-b border-gray-800">
          <div className="text-xs text-gray-400">Message de {msg.est_de_moi ? 'vous' : `${msg.expediteur.prenoms} ${msg.expediteur.nom}`}</div>
        </div>
        <button onClick={onReply} className="w-full px-4 py-3 text-left text-sm text-gray-100 hover:bg-gray-800 flex items-center gap-3">
          <Reply size={16} className="text-blue-400" /> Répondre
        </button>
        <button onClick={onForward} className="w-full px-4 py-3 text-left text-sm text-gray-100 hover:bg-gray-800 flex items-center gap-3">
          <Forward size={16} className="text-green-400" /> Transférer
        </button>
        <button onClick={onEpingler} className="w-full px-4 py-3 text-left text-sm text-gray-100 hover:bg-gray-800 flex items-center gap-3">
          <Pin size={16} className="text-yellow-400" /> {msg.epingle ? 'Désépingler' : 'Épingler'}
        </button>
        {msg.est_de_moi ? (
          <button onClick={onSupprimer} className="w-full px-4 py-3 text-left text-sm text-red-400 hover:bg-gray-800 flex items-center gap-3">
            <Trash2 size={16} /> Supprimer
          </button>
        ) : (
          <button onClick={onSignaler} className="w-full px-4 py-3 text-left text-sm text-orange-400 hover:bg-gray-800 flex items-center gap-3">
            <Flag size={16} /> Signaler
          </button>
        )}
        <button onClick={onClose} className="w-full px-4 py-3 text-center text-sm text-gray-400 hover:bg-gray-800 border-t border-gray-800">
          Annuler
        </button>
      </div>
    </div>
  );
};

// ─── Modal Transférer ──────────────────────────────────────────────
const ForwardModal: React.FC<{
  msg: ChatMessageItem;
  conversations: ChatConversationItem[];
  onClose: () => void;
  onSent: () => void;
}> = ({ msg, conversations, onClose, onSent }) => {
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [sending, setSending] = useState(false);

  const toggle = (id: number) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const envoyer = async () => {
    if (selected.size === 0 || sending) return;
    setSending(true);
    try {
      const r = await portailApi.chatTransfererMessage(msg.id, {
        conversation_ids: Array.from(selected),
      });
      alert(`Message transféré à ${r.nb_envoyes} conversation(s).`);
      onSent();
    } catch (e: any) {
      alert(e?.message || 'Erreur');
    } finally {
      setSending(false);
    }
  };

  return (
    <div onClick={onClose} className="fixed inset-0 z-[55] bg-black/60 flex items-center justify-center p-4">
      <div onClick={e => e.stopPropagation()} className="bg-gray-900 border border-gray-800 rounded-xl w-full max-w-md max-h-[80vh] flex flex-col">
        <div className="px-4 py-3 border-b border-gray-800 flex items-center justify-between">
          <div className="text-sm font-semibold text-gray-100">Transférer à…</div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {conversations.length === 0 ? (
            <div className="text-xs text-gray-500 text-center py-8">Aucune autre conversation</div>
          ) : conversations.map(c => (
            <button
              key={c.id}
              onClick={() => toggle(c.id)}
              className={`w-full flex items-center gap-2.5 px-3 py-2 rounded hover:bg-gray-800 ${
                selected.has(c.id) ? 'bg-blue-900/30' : ''
              }`}
            >
              <input type="checkbox" readOnly checked={selected.has(c.id)} className="pointer-events-none" />
              {c.avatar_url ? (
                <img src={c.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-semibold">
                  {c.titre.split(' ').map(s => s[0]).slice(0, 2).join('')}
                </div>
              )}
              <div className="flex-1 min-w-0 text-left">
                <div className="text-sm text-gray-100 truncate">{c.titre}</div>
                <div className="text-[10px] text-gray-500 truncate">{c.sous_titre}</div>
              </div>
            </button>
          ))}
        </div>
        <div className="px-4 py-2 border-t border-gray-800 flex items-center gap-2 justify-end">
          <button onClick={onClose} className="px-3 py-1.5 text-sm text-gray-300 hover:text-white">Annuler</button>
          <button
            onClick={envoyer}
            disabled={selected.size === 0 || sending}
            className="px-4 py-1.5 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded text-sm inline-flex items-center gap-2"
          >
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Forward size={14} />}
            Transférer ({selected.size})
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Modal photo de profil agrandie ─────────────────────────────
const PhotoModal: React.FC<{
  etu: ChatEtudiantSummary;
  onClose: () => void;
}> = ({ etu, onClose }) => {
  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-4"
    >
      <button
        onClick={onClose}
        className="absolute top-3 right-3 text-white/80 hover:text-white text-3xl leading-none"
        aria-label="Fermer"
      >×</button>
      <div onClick={(e) => e.stopPropagation()} className="flex flex-col items-center gap-3 max-w-md w-full">
        {etu.photo_url ? (
          <img src={etu.photo_url} alt="" className="max-w-full max-h-[80vh] rounded-lg object-contain" />
        ) : (
          <div className="w-48 h-48 rounded-full bg-blue-600 text-white flex items-center justify-center text-4xl font-bold">
            {(etu.prenoms?.[0] || '') + (etu.nom?.[0] || '')}
          </div>
        )}
        <div className="text-center text-white">
          <div className="font-semibold text-base">{etu.prenoms} {etu.nom}</div>
          <div className="text-xs text-gray-300 mt-0.5">
            {etu.classe || ''}{etu.option ? ` · ${etu.option}` : ''}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Modal Créer un groupe ─────────────────────────────────────

const CreateGroupModal: React.FC<{
  onClose: () => void;
  onCreated: (convId: number) => void;
}> = ({ onClose, onCreated }) => {
  const [nom, setNom] = useState('');
  const [q, setQ] = useState('');
  const [items, setItems] = useState<(ChatEtudiantSummary & { en_ligne: boolean })[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    portailApi.chatAnnuaire({ q: q || undefined, limit: 100 })
      .then((res) => { if (mounted) setItems(res.results || []); })
      .catch(() => { if (mounted) setItems([]); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [q]);

  const toggle = (matricule: string) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(matricule)) n.delete(matricule); else n.add(matricule);
      return n;
    });
  };

  const creer = async () => {
    setError(null);
    if (!nom.trim()) { setError('Le nom du groupe est requis'); return; }
    if (selected.size < 1) { setError('Sélectionnez au moins 1 camarade'); return; }
    setCreating(true);
    try {
      const res = await portailApi.chatCreerGroupe(nom.trim(), Array.from(selected));
      onCreated(res.conversation_id);
    } catch (e: any) {
      setError(e?.message || 'Erreur création');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-3">
      <div className="bg-gray-900 rounded-t-xl sm:rounded-xl shadow-xl max-w-md w-full max-h-[90vh] flex flex-col">
        <div className="px-4 py-3 border-b border-gray-800 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-100 flex items-center gap-2">
            <Users size={16} className="text-purple-400" /> Nouveau groupe
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={18} /></button>
        </div>
        <div className="px-3 py-2 border-b border-gray-800 space-y-2">
          <input
            type="text"
            value={nom}
            onChange={(e) => setNom(e.target.value)}
            placeholder="Nom du groupe (ex : Projet IA L3)"
            maxLength={100}
            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-sm text-gray-100 placeholder-gray-500 focus:outline-none focus:border-purple-500"
            autoFocus
          />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher des camarades à ajouter…"
            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-sm text-gray-100 placeholder-gray-500 focus:outline-none focus:border-blue-500"
          />
          {selected.size > 0 && (
            <div className="text-[11px] text-purple-300">
              {selected.size} camarade(s) sélectionné(s)
            </div>
          )}
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading && <div className="p-4 text-xs text-gray-500 text-center">…</div>}
          {!loading && items.length === 0 && <div className="p-4 text-xs text-gray-500 text-center">Aucun résultat</div>}
          {items.map((etu) => {
            const id = (etu as any).matricule_reel || etu.matricule;
            const sel = selected.has(id);
            return (
              <button
                key={id}
                onClick={() => toggle(id)}
                className={`w-full px-3 py-2 flex items-center gap-3 border-b border-gray-800 text-left ${
                  sel ? 'bg-purple-900/30' : 'hover:bg-gray-800/50'
                }`}
              >
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                  sel ? 'bg-purple-600 border-purple-500' : 'border-gray-600'
                }`}>
                  {sel && <span className="text-white text-xs">✓</span>}
                </div>
                {etu.photo_url ? (
                  <img src={etu.photo_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-[11px] font-semibold">
                    {(etu.prenoms[0] || '') + (etu.nom[0] || '')}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-gray-100 truncate">{etu.prenoms} {etu.nom}</div>
                  <div className="text-[10px] text-gray-500 truncate">{etu.matricule_masque ? '' : `${etu.matricule} · `}{etu.classe}</div>
                </div>
              </button>
            );
          })}
        </div>
        {error && (
          <div className="px-3 py-2 text-xs text-red-300 bg-red-900/30 border-t border-red-800">{error}</div>
        )}
        <div className="px-3 py-2 border-t border-gray-800 flex items-center gap-2 justify-end">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-gray-300 hover:text-white"
          >Annuler</button>
          <button
            onClick={creer}
            disabled={creating || !nom.trim() || selected.size === 0}
            className="px-4 py-1.5 text-sm bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white rounded inline-flex items-center gap-1.5 font-medium"
          >
            {creating ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />}
            {creating ? 'Création…' : `Créer (${selected.size})`}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Modal annuaire camarades ──────────────────────────────────

const AnnuaireModal: React.FC<{
  onClose: () => void;
  onSelect: (matricule: string) => void;
}> = ({ onClose, onSelect }) => {
  const [q, setQ] = useState('');
  const [items, setItems] = useState<(ChatEtudiantSummary & { en_ligne: boolean })[]>([]);
  const [loading, setLoading] = useState(true);
  const [maClasse, setMaClasse] = useState('');

  const load = async (query: string) => {
    setLoading(true);
    try {
      const res = await portailApi.chatAnnuaire({ q: query || undefined, limit: 100 });
      setItems(res.results || []);
      setMaClasse(res.ma_classe || '');
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(''); }, []);
  useEffect(() => {
    const t = setTimeout(() => load(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-3">
      <div className="bg-gray-900 rounded-t-xl sm:rounded-xl shadow-xl max-w-md w-full max-h-[90vh] flex flex-col">
        <div className="px-4 py-3 border-b border-gray-800 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-100">Choisir un camarade</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={18} /></button>
        </div>
        <div className="px-3 py-2 border-b border-gray-800">
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher (nom, matricule…)"
            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-sm text-gray-100 placeholder-gray-500 focus:outline-none focus:border-blue-500"
            autoFocus
          />
          {!q && maClasse && (
            <div className="mt-1.5 text-[10px] text-gray-500">
              Affiche les camarades de votre classe ({maClasse}). Tapez pour chercher au-delà.
            </div>
          )}
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading && <div className="p-4 text-xs text-gray-500 text-center">Recherche…</div>}
          {!loading && items.length === 0 && <div className="p-6 text-xs text-gray-500 text-center">Aucun résultat</div>}
          {items.map((etu) => (
            <button
              key={(etu as any).matricule_reel || etu.matricule}
              onClick={() => onSelect((etu as any).matricule_reel || etu.matricule)}
              className="w-full px-3 py-2.5 flex items-center gap-3 hover:bg-gray-800/50 border-b border-gray-800 text-left"
            >
              <div className="relative">
                {etu.photo_url ? (
                  <img src={etu.photo_url} alt="" className="w-9 h-9 rounded-full object-cover" />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-semibold">
                    {(etu.prenoms[0] || '') + (etu.nom[0] || '')}
                  </div>
                )}
                {etu.en_ligne && <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 border-2 border-gray-900 rounded-full" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-100 truncate">{etu.prenoms} {etu.nom}</div>
                <div className="text-[11px] text-gray-500 truncate">{etu.matricule_masque ? '' : `${etu.matricule} · `}{etu.classe}</div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─── Lecteur de message vocal ────────────────────────────────────
const AudioMessage: React.FC<{
  url: string;
  dureeSec: number;
  estDeMoi: boolean;
}> = ({ url, dureeSec, estDeMoi }) => {
  const audioRef = React.useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = React.useState(false);
  const [position, setPosition] = React.useState(0);
  const [duration, setDuration] = React.useState(dureeSec);

  React.useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onEnd = () => { setPlaying(false); setPosition(0); };
    const onTime = () => setPosition(a.currentTime);
    const onMeta = () => {
      if (a.duration && isFinite(a.duration)) setDuration(Math.round(a.duration));
    };
    a.addEventListener('ended', onEnd);
    a.addEventListener('timeupdate', onTime);
    a.addEventListener('loadedmetadata', onMeta);
    return () => {
      a.removeEventListener('ended', onEnd);
      a.removeEventListener('timeupdate', onTime);
      a.removeEventListener('loadedmetadata', onMeta);
    };
  }, []);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else { a.play(); setPlaying(true); }
  };

  const fmt = (s: number) => {
    const m = Math.floor(s / 60);
    const r = Math.floor(s % 60);
    return `${m}:${String(r).padStart(2, '0')}`;
  };

  const total = duration || dureeSec || 1;
  const progress = total > 0 ? Math.min(100, (position / total) * 100) : 0;

  return (
    <div className="flex items-center gap-2 min-w-[180px] py-1">
      <audio ref={audioRef} src={url} preload="metadata" className="hidden" />
      <button
        onClick={toggle}
        className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
          estDeMoi ? 'bg-blue-700 hover:bg-blue-800' : 'bg-gray-700 hover:bg-gray-600'
        }`}
        title={playing ? 'Pause' : 'Lire'}
      >
        {playing ? <Pause size={14} /> : <Play size={14} className="ml-0.5" />}
      </button>
      <div className="flex-1 min-w-[80px]">
        <div className={`h-1 rounded-full overflow-hidden ${estDeMoi ? 'bg-blue-800' : 'bg-gray-700'}`}>
          <div
            className={`h-full ${estDeMoi ? 'bg-blue-200' : 'bg-blue-400'}`}
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className={`text-[10px] mt-0.5 tabular-nums ${estDeMoi ? 'text-blue-100' : 'text-gray-400'}`}>
          {fmt(position)} / {fmt(total)}
        </div>
      </div>
    </div>
  );
};
